import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, AlertTriangle, ClipboardList, FileX } from 'lucide-react';
import { api } from '../../lib/api';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { formatDate } from '../../lib/format';

interface QueueRow {
  id: string; name: string; country_code: string; verification_status: string;
  plan: string; jp_corp_number: string | null; jp_invoice_registration: string | null;
  industry: string | null; url: string | null; created_at: string;
}
interface Overview {
  pendingCompanies: number; pendingInvoices: number; pendingApprovals: number; disputedInvoices: number;
}
interface Note {
  id: string; company_id: string; reviewer_user_id: string | null;
  decision: string | null; note: string | null; created_at: string;
}

/**
 * Admin verification queue. Lives at /admin and is guarded by the
 * backend's AdminGuard — anyone hitting this page without admin
 * permissions sees a 403 from the queue endpoint and the list stays
 * empty. (Frontend doesn't try to be a second auth layer.)
 */
export function AdminQueuePage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const overview = useQuery<Overview>({
    queryKey: ['admin-overview'],
    queryFn: async () => (await api.get('/admin/overview')).data,
    refetchInterval: 60_000,
  });
  const queue = useQuery<QueueRow[]>({
    queryKey: ['admin-queue'],
    queryFn: async () => (await api.get('/admin/verification-queue')).data,
  });
  const notes = useQuery<Note[]>({
    queryKey: ['admin-notes', selected],
    enabled: !!selected,
    queryFn: async () => (await api.get(`/admin/companies/${selected}/notes`)).data,
  });
  const decide = useMutation({
    mutationFn: async (input: { id: string; decision: 'approved' | 'rejected' | 'more_info' }) =>
      (await api.post(`/admin/companies/${input.id}/decide`, {
        decision: input.decision,
        note: note.trim() || undefined,
      })).data,
    onSuccess: () => {
      setNote('');
      qc.invalidateQueries({ queryKey: ['admin-queue'] });
      qc.invalidateQueries({ queryKey: ['admin-overview'] });
      qc.invalidateQueries({ queryKey: ['admin-notes', selected] });
    },
  });

  const focused = queue.data?.find((r) => r.id === selected) ?? queue.data?.[0];

  return (
    <AdminShell>
      <div className="px-8 py-7 max-w-7xl w-full">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Tile icon={ClipboardList} label={t('admin.tiles.pendingCompanies')} value={overview.data?.pendingCompanies ?? 0} />
          <Tile icon={FileX} label={t('admin.tiles.pendingInvoices')} value={overview.data?.pendingInvoices ?? 0} />
          <Tile icon={ShieldCheck} label={t('admin.tiles.pendingApprovals')} value={overview.data?.pendingApprovals ?? 0} />
          <Tile icon={AlertTriangle} label={t('admin.tiles.disputedInvoices')} value={overview.data?.disputedInvoices ?? 0} />
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <Card className="lg:col-span-1">
            <CardHeader>
              <div className="font-medium">{t('admin.queueTitle')}</div>
            </CardHeader>
            {queue.isLoading ? (
              <div className="p-10 text-center text-muted-foreground">{t('common.loading')}</div>
            ) : !queue.data || queue.data.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground text-sm">{t('admin.queueEmpty')}</div>
            ) : (
              <ul className="divide-y divide-border max-h-[600px] overflow-y-auto">
                {queue.data.map((r) => (
                  <li
                    key={r.id}
                    className={
                      'px-5 py-3 cursor-pointer hover:bg-muted/60 ' +
                      (focused?.id === r.id ? 'bg-muted' : '')
                    }
                    onClick={() => setSelected(r.id)}
                  >
                    <div className="font-medium text-sm">{r.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                      <Pill tone="muted">{r.country_code}</Pill>
                      <span className="num">{formatDate(r.created_at, i18n.language)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="lg:col-span-2">
            {focused ? (
              <>
                <CardHeader>
                  <div className="font-medium">{focused.name}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                    <Pill tone={focused.verification_status === 'verified' ? 'success' : 'accent'}>
                      {focused.verification_status}
                    </Pill>
                    <span>{focused.country_code}</span>
                    {focused.industry && <span>· {focused.industry}</span>}
                  </div>
                </CardHeader>
                <CardBody>
                  <dl className="grid grid-cols-2 gap-y-3 text-sm">
                    <Row label={t('admin.fields.plan')} value={focused.plan} />
                    <Row label={t('admin.fields.url')} value={focused.url ?? '—'} />
                    <Row label={t('admin.fields.jpCorpNumber')} value={focused.jp_corp_number ?? '—'} mono />
                    <Row label={t('admin.fields.jpInvoiceRegistration')} value={focused.jp_invoice_registration ?? '—'} mono />
                  </dl>

                  <div className="mt-6">
                    <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                      {t('admin.notePlaceholder')}
                    </label>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={3}
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                    />
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => decide.mutate({ id: focused.id, decision: 'approved' })}
                      disabled={decide.isPending}
                      className="px-4 py-2 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90"
                    >
                      {t('admin.actions.approve')}
                    </button>
                    <button
                      onClick={() => decide.mutate({ id: focused.id, decision: 'more_info' })}
                      disabled={decide.isPending}
                      className="px-4 py-2 rounded-lg border border-border text-sm font-medium"
                    >
                      {t('admin.actions.moreInfo')}
                    </button>
                    <button
                      onClick={() => decide.mutate({ id: focused.id, decision: 'rejected' })}
                      disabled={decide.isPending}
                      className="px-4 py-2 rounded-lg border border-destructive text-destructive text-sm font-medium"
                    >
                      {t('admin.actions.reject')}
                    </button>
                  </div>

                  <div className="mt-6">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
                      {t('admin.historyTitle')}
                    </div>
                    {notes.isLoading ? (
                      <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
                    ) : notes.data?.length === 0 ? (
                      <div className="text-sm text-muted-foreground">{t('admin.historyEmpty')}</div>
                    ) : (
                      <ul className="space-y-2 text-sm">
                        {notes.data?.map((n) => (
                          <li key={n.id} className="rounded-lg border border-border px-3 py-2">
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span>{n.decision ?? '—'}</span>
                              <span className="num">{formatDate(n.created_at, i18n.language)}</span>
                            </div>
                            {n.note && <div className="mt-1">{n.note}</div>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </CardBody>
              </>
            ) : (
              <div className="p-10 text-center text-muted-foreground">{t('admin.selectCompany')}</div>
            )}
          </Card>
        </div>
      </div>
    </AdminShell>
  );
}

function Tile({ icon: Icon, label, value }: { icon: typeof ShieldCheck; label: string; value: number }) {
  return (
    <Card className="p-4 flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg bg-accent-50 text-accent-700 flex items-center justify-center">
        <Icon size={16} />
      </div>
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="num text-xl font-semibold">{value}</div>
      </div>
    </Card>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={mono ? 'font-mono mt-1' : 'mt-1'}>{value}</div>
    </div>
  );
}
